import React, { useState, useEffect, useContext, useMemo } from 'react';
import PropTypes from 'prop-types';
import UserProp from '../../../../commonComponents/propTypes/userProp';
import translate from '../../../../commonComponents/translate';
import Form from '../../../../commonComponents/forms/Form';
import Separator from '../../../../commonComponents/forms/Separator';
import FormFooter from '../../../../commonComponents/forms/FormFooter';
import FormSubtitle from '../../../../commonComponents/forms/FormSubtitle';
import ImageInput from '../../../../commonComponents/forms/ImageInput';
import {
  compose,
  handleImageUpload,
  PROFILE_BLOCK,
  toPersistableVideo,
  VIDEO_UPLOAD_CONTEXT,
} from '../../../../services/utilityFunctions';
import withCSRF from '../../../../commonComponents/withCSRF';
import SkipStepButton from '../../../journey/overall/content/components/SkipStepButton';
import ConfigsContext from '../../../../contextProviders/ConfigsContext';
import {
  checkIfHasErrors,
  getErrors,
  getValidationRules,
  isFieldRequiredLabel,
} from '../../helpers/helper';
import ErrorMessage from '../../../../commonComponents/forms/ErrorMessage';

function initialProps() {
  return {
    avatar: { blob: null, fileName: '' },
    video: { blob: null, fileName: '' },
    image0: { blob: null, fileName: '' },
    image1: { blob: null, fileName: '' },
    image2: { blob: null, fileName: '' },
    image3: { blob: null, fileName: '' },
    image4: { blob: null, fileName: '' },
  };
}

/**
 * Form to update the current logged user
 */
function MediaForm({ t, _csrf, member, onMemberChange, onSkipStep }) {
  const { organization } = useContext(ConfigsContext);
  const { requiredFieldsPerBlock = [] } =
    organization?.services?.me?.config || {};
  const [state, setState] = useState(initialProps());
  const [imageTouched, setImageTouched] = useState({
    avatar: false,
    video: false,
    image0: false,
    image1: false,
    image2: false,
    image3: false,
    image4: false,
  });
  const [errors, setErrors] = useState({
    image: false,
    video: false,
    images: false,
  });

  useEffect(() => setState(initialProps()), [member]);

  const formId = 'mediaForm';
  const validationRules = useMemo(
    () =>
      getValidationRules(
        requiredFieldsPerBlock,
        PROFILE_BLOCK.MEDIA,
        {},
        {},
        0,
      ),
    [getValidationRules, requiredFieldsPerBlock],
  );

  const imageUpload = async (name, original, isVideo) => {
    if (!imageTouched[name]) {
      const result = original;
      if (result && result._id) {
        delete result._id;
      }
      return result;
    }
    // BRAVON-6332 Phase 2b — `User.video` is personal data, so the upload asks
    // for the private bucket. A server that honours it answers
    // `{url: null, private: true, receipt, ...}`; `toPersistableVideo` keeps
    // the receipt (the save exchanges it for the pointer) and drops `storage`
    // / `previewUrl`, which must never be echoed back. A public answer — every
    // answer while the flags are off — is returned untouched.
    const uploaded = await handleImageUpload(
      t,
      _csrf,
      state[name],
      isVideo,
      isVideo ? VIDEO_UPLOAD_CONTEXT.USER : null,
    );
    return isVideo ? toPersistableVideo(uploaded) : uploaded;
  };

  const imageUploads = () => {
    const images = member.images || [];
    const requests = [imageUpload('avatar', member.image, false)];
    requests.push(imageUpload('video', member.video, true));
    [0, 1, 2, 3, 4].forEach(i =>
      requests.push(imageUpload(`image${i}`, images[i], false)),
    );
    return Promise.all(requests);
  };

  const handleSubmit = async () => {
    const [avatar, video, ...images] = await imageUploads();
    const newErrors = getErrors(validationRules, {
      image: avatar,
      video,
      images: [...images?.filter(i => i)],
    });

    if (checkIfHasErrors(newErrors)) {
      setErrors(newErrors);
      return;
    }

    const params = {
      image: avatar,
      video,
      images: [...images].filter(i => !!i),
    };
    onMemberChange(params);
  };

  const handleImageChange = field => value => {
    setImageTouched(prev => ({ ...prev, [field]: true }));
    setState(prevState => ({ ...prevState, [field]: value }));
  };

  const cropSquare = { cropAspect: 1 };
  const cropRect = { cropAspect: 2.0588 };

  return (
    <Form
      className="m-form m-form--fit m-form--label-align-right"
      id={formId}
      submitMethod={handleSubmit}
      validationRules={validationRules}
    >
      {submitHandler => (
        <>
          <div className="m-portlet__body">
            <div className="row profile-skipStep">
              <div className={`${!onSkipStep ? 'col-12' : 'col-6'}`}>
                <FormSubtitle
                  title={isFieldRequiredLabel(
                    requiredFieldsPerBlock,
                    PROFILE_BLOCK.MEDIA,
                    'image',
                    t('image'),
                    0,
                  )}
                  titleClass={errors?.image ? 'label-error' : ''}
                />
              </div>
              {onSkipStep && (
                <div className="col-6 skip-wrapper btn-wrapper">
                  <SkipStepButton onClick={onSkipStep} />
                </div>
              )}
            </div>
            <ImageInput
              id="avatar"
              inputId="avatar"
              name="avatar"
              type="image"
              placeholder="/images/no_image_upload.png"
              initialValue={member.image && member.image.url}
              onChange={handleImageChange('avatar')}
              inputProps={cropSquare}
              label=""
            />
            {errors?.image && <ErrorMessage />}
            <Separator />
            <FormSubtitle
              title={isFieldRequiredLabel(
                requiredFieldsPerBlock,
                PROFILE_BLOCK.MEDIA,
                'video',
                t('profile_video'),
                0,
              )}
              titleClass={errors?.video ? 'label-error' : ''}
            />
            <ImageInput
              id="video"
              inputId="videoInput"
              name="video"
              type="video"
              placeholder="/images/no_video_upload.png"
              initialValue={member.video && member.video.url}
              onChange={handleImageChange('video')}
              label=""
            />
            {errors?.video && <ErrorMessage />}
            <Separator />
            <FormSubtitle
              title={isFieldRequiredLabel(
                requiredFieldsPerBlock,
                PROFILE_BLOCK.MEDIA,
                'images',
                t('profile_images'),
              )}
              titleClass={errors?.images ? 'label-error' : ''}
            />
            {errors?.images && <ErrorMessage />}
            <div className="row">
              <div className="col-10 offset-2 row">
                {[0, 1, 2, 3, 4].map(i => (
                  <div key={i} className="col-12 col-lg-4">
                    <ImageInput
                      id={`image${i}`}
                      inputId={`image${i}Input`}
                      name={`image${i}`}
                      placeholder="/images/no_image_upload.png"
                      initialValue={
                        member.images &&
                        member.images[i] &&
                        member.images[i].url
                      }
                      onChange={handleImageChange(`image${i}`)}
                      inputProps={cropRect}
                      label=""
                      groupClassName=""
                      labelClassName=""
                      containerClassName=""
                    />
                  </div>
                ))}
              </div>
            </div>
          </div>
          <FormFooter submitHandler={submitHandler} />
        </>
      )}
    </Form>
  );
}

MediaForm.propTypes = {
  /** translation function */
  t: PropTypes.func.isRequired,
  _csrf: PropTypes.string.isRequired,
  /** member being edited */
  member: PropTypes.shape(UserProp.propType).isRequired,
  /** member change event handler */
  onMemberChange: PropTypes.func.isRequired,
  onSkipStep: PropTypes.func,
};

MediaForm.defaultProps = {
  onSkipStep: null,
};

export default compose(translate, withCSRF)(MediaForm);
